import {
  constructOrgUserTargetId,
  type FeatureFlagKey,
  TARGET_ORG_USER,
} from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import {
  getUserFeatureFlagsQueryKey,
  type UserFeatureFlagsResponse,
} from './get-user-feature-flags-admin';

import type { FlagTargetState } from '@nohara-dxdev/feature-flags';

export interface ToggleUserFeatureFlagInput {
  flagKey: FeatureFlagKey;
  userId: string;
  enabled: boolean;
}

interface OptimisticContext {
  previous: UserFeatureFlagsResponse | undefined;
  queryKey: ReturnType<typeof getUserFeatureFlagsQueryKey>;
}

const toggleUserFeatureFlag = async (
  orgId: string,
  input: ToggleUserFeatureFlagInput,
): Promise<{ success: boolean }> => {
  const { data } = await api.put<unknown, ApiResponse<{ success: boolean }>>(
    `/orgs/${orgId}/users/${input.userId}/feature-flags/${input.flagKey}`,
    { enabled: input.enabled },
  );
  return data;
};

const upsertUserRow = (
  current: UserFeatureFlagsResponse | undefined,
  orgUserTargetId: string,
  input: ToggleUserFeatureFlagInput,
): UserFeatureFlagsResponse => {
  const others = (current?.flags ?? []).filter(
    (s) =>
      !(
        s.flagKey === input.flagKey &&
        s.targetType === TARGET_ORG_USER &&
        s.targetId === orgUserTargetId
      ),
  );
  const next: FlagTargetState = {
    flagKey: input.flagKey,
    targetType: TARGET_ORG_USER,
    targetId: orgUserTargetId,
    enabled: input.enabled,
    updatedAt: Date.now(),
    updatedBy: 'optimistic',
  };
  return { flags: [...others, next] };
};

interface UseToggleUserFeatureFlagOptions {
  mutationConfig?: MutationConfig<
    (input: ToggleUserFeatureFlagInput) => Promise<{ success: boolean }>
  >;
}

export const useToggleUserFeatureFlag = ({
  mutationConfig,
}: UseToggleUserFeatureFlagOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId } = useScopeIds('org');

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation<{ success: boolean }, Error, ToggleUserFeatureFlagInput, OptimisticContext>({
    ...restConfig,
    onMutate: async (input) => {
      const queryKey = getUserFeatureFlagsQueryKey(orgId, input.userId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<UserFeatureFlagsResponse>(queryKey);
      const orgUserTargetId = constructOrgUserTargetId(orgId, input.userId);
      queryClient.setQueryData<UserFeatureFlagsResponse>(queryKey, (old) =>
        upsertUserRow(old, orgUserTargetId, input),
      );
      return { previous, queryKey };
    },
    onSuccess: (data, vars, ctx, mutationContext) => {
      notifications.success('featureFlags.toggle.success');
      onSuccess?.(data, vars, ctx, mutationContext);
    },
    onError: (error, vars, ctx, mutationContext) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(ctx.queryKey, ctx.previous);
      }
      notifications.error('featureFlags.toggle.error');
      onError?.(error, vars, ctx, mutationContext);
    },
    onSettled: () => {
      // Admin cache is patched optimistically — no refetch.
      // The `me` query drives FAB visibility across the app and is a separate concern.
      void queryClient.invalidateQueries({ queryKey: ['feature-flags', 'me', orgId] });
    },
    mutationFn: (input) => toggleUserFeatureFlag(orgId, input),
  });
};
